import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import type { EstudoAtual, PainelDaHome } from "@noesis/shared";
import { pedir } from "../api.js";
import { useRitual } from "../estado/ritual.js";
import { useTour } from "../estado/tour.js";
import { Icone } from "./Icone.js";
import { Risquinhos } from "./Risquinhos.js";
import { Semana, fraseDoDescanso } from "./Semana.js";
import { IconeIa } from "./icones.js";
import { usarPausaDeMovimento } from "./modo-sessao.js";

/**
 * O que a tela guardou de cada resposta desta sessão, para o fim (8d).
 *
 * Mora no navegador e some ao recarregar: é o resumo da sessão que acabou de
 * acontecer, não registro. O registro é a `resposta`, no banco (invariante 12).
 * Sem ele o fim mostra só o que o servidor sabe — certas e quantas foram para
 * a fila.
 */
export type RegistroDaSessao = {
  itemId: string;
  enunciado: string;
  categoria: string[];
  acertou: boolean;
  tempoMs: number;
  riscadas: number;
};

/**
 * Fim de sessão — 8d do Caderno.
 *
 * Quando a meta do dia foi cumprida, a folha vira a sequência coreografada do
 * handoff: a página vira, os blocos sobem em cascata, os risquinhos crescem um
 * a um, o post-it cai girando, o visto da meta se desenha, o número antigo dos
 * dias seguidos é riscado e o novo aparece, e por fim o check do dia na
 * fileira da semana. Os tempos estão na folha (`caderno.css`), e sob
 * `prefers-reduced-motion` tudo nasce no lugar.
 *
 * Sem meta cumprida é a mesma página, sem a festa: "Sessão concluída" e o que
 * falta para a meta.
 */
/** O que o fim precisa saber da sessão que acabou. */
export type ResumoDoFim = {
  respondidos: number;
  acertos: number;
  iniciadaEm: string | null;
  respondidosHoje: number;
  metaDiaria: number;
};

/** O resumo a partir do estado do servidor (sessão aberta sem item). */
export function resumoDoAtual(atual: EstudoAtual): ResumoDoFim {
  const p = atual.sessao?.progresso;
  return {
    respondidos: p?.respondidos ?? 0,
    acertos: p?.acertos ?? 0,
    iniciadaEm: atual.sessao?.iniciadaEm ?? null,
    respondidosHoje: atual.respondidosHoje,
    metaDiaria: atual.metaDiaria,
  };
}

export function FimDeSessao({
  atual,
  registros,
  aoEstudarMais,
  aoEncerrar,
  encerrando,
}: {
  atual: ResumoDoFim;
  registros: readonly RegistroDaSessao[];
  aoEstudarMais: () => void;
  aoEncerrar: () => void;
  encerrando: boolean;
}) {
  // O fim é a folha inteira, como a sessão: sem lateral nem ilha.
  usarPausaDeMovimento();
  const { definir } = useRitual();
  const { informar, avancarSe } = useTour();
  const [painel, setPainel] = useState<PainelDaHome | null>(null);
  const titulo = useRef<HTMLHeadingElement>(null);
  const p = atual;
  const cumpriu = atual.respondidosHoje >= atual.metaDiaria;
  const erradas = registros.filter((r) => !r.acertou);
  const tempoMedio =
    registros.length > 0 ? Math.round(registros.reduce((s, r) => s + r.tempoMs, 0) / registros.length / 1000) : null;
  const riscadas = registros.reduce((s, r) => s + r.riscadas, 0);
  const inicio = atual.iniciadaEm ? Date.parse(atual.iniciadaEm) : null;
  const [duracao] = useState(() => (inicio ? Math.max(0, Math.floor((Date.now() - inicio) / 1000)) : null));

  useEffect(() => {
    let vivo = true;
    void pedir("home.painel")
      .then((r) => {
        if (!vivo) return;
        setPainel(r);
        definir(r.ofensiva);
      })
      .catch(() => undefined);
    titulo.current?.focus();
    return () => {
      vivo = false;
    };
  }, [definir]);

  const ofensiva = painel?.ofensiva ?? null;
  /**
   * A trilha acabou por hoje: nenhuma questão nova e nada vencendo (ADR 041).
   * "Estudar mais" montaria uma sessão vazia; o que resta fazer é trazer
   * questões. Enquanto o painel não chega, vale o caminho de sempre.
   */
  const esgotada = painel !== null && painel.itensVistos >= painel.itensTotal && painel.vencemHoje === 0;
  const faltam = Math.max(atual.metaDiaria - atual.respondidosHoje, 0);

  useEffect(() => informar({ meta: atual.metaDiaria, faltam }), [informar, atual.metaDiaria, faltam]);

  /** Encerrar o dia é o gesto que o tour espera no fim da sessão (ADR 041). */
  function encerrar() {
    avancarSe("encerrar");
    aoEncerrar();
  }
  const antes = ofensiva ? Math.max(ofensiva.atual - 1, 0) : null;
  const descanso = painel ? fraseDoDescanso(painel.diasDeDescanso) : null;

  return (
    <div className={cumpriu ? "fim fim--festa" : "fim"}>
      <header className="fim__topo">
        <button type="button" className="botao-icone fim__fechar" aria-label="Encerrar o dia" onClick={encerrar}>
          <Icone glifo="x" papel="acao" />
        </button>
        <p className="t-anotacao t-forte fim__rotulo">sessão concluída</p>
        {duracao !== null && <p className="t-anotacao t-2 fim__total">{comoDuracao(duracao)} no total</p>}
      </header>

      <div className="fim__grade">
        <div className="fim__principal">
          <h1 className="t-titulo fim__bloco" tabIndex={-1} ref={titulo}>
            {cumpriu ? "Meta de hoje cumprida" : "Sessão concluída"}
          </h1>
          <p className="t-ajuda fim__bloco">
            {p.respondidos} {p.respondidos === 1 ? "questão" : "questões"} nesta sessão, {atual.respondidosHoje} de{" "}
            {atual.metaDiaria} no dia.
            {!cumpriu && ` Faltam ${atual.metaDiaria - atual.respondidosHoje} para a meta.`}
          </p>

          <div className="fim__risquinhos fim__bloco" aria-hidden="true">
            <Risquinhos quantidade={Math.min(atual.respondidosHoje, atual.metaDiaria)} animar={cumpriu} maximo={atual.metaDiaria} className="fim__tally" />
            {cumpriu && (
              <svg className="fim__visto" viewBox="0 0 140 110">
                <path pathLength={100} d="M8 62 C22 74 34 88 42 104 C62 58 94 22 134 6" />
              </svg>
            )}
          </div>

          <dl className="fim__numeros fim__bloco">
            <div className="fim__linha">
              <dt className="t-ajuda">certas</dt>
              <dd className="t-secao">
                {p.acertos} de {p.respondidos}
              </dd>
            </div>
            {tempoMedio !== null && (
              <div className="fim__linha">
                <dt className="t-ajuda">tempo médio por item</dt>
                <dd className="t-secao">{tempoMedio} s</dd>
              </div>
            )}
            {registros.length > 0 && (
              <div className="fim__linha">
                <dt className="t-ajuda">riscadas antes de responder</dt>
                <dd className="t-secao">
                  {riscadas} {riscadas === 1 ? "alternativa" : "alternativas"}
                </dd>
              </div>
            )}
            <div className="fim__linha">
              <dt className="t-ajuda">foram para Revisar</dt>
              <dd className="t-secao">{p.respondidos - p.acertos}</dd>
            </div>
          </dl>

          {erradas.length > 0 && (
            <section className="fim__erradas fim__bloco" aria-labelledby="erradas-titulo">
              <h2 className="t-secao" id="erradas-titulo">
                {erradas.length === 1 ? "A que você errou" : erradas.length === 2 ? "As duas que você errou" : `As ${erradas.length} que você errou`}
              </h2>
              <ul>
                {erradas.map((r) => (
                  <li key={r.itemId} className="fim__errada">
                    <p className="t-anotacao-p">{r.categoria.join(", ")}</p>
                    <div className="fim__errada-linha">
                      <p className="t-ajuda fim__enunciado">{r.enunciado}</p>
                      <Link className="link-mao" to="/revisar">
                        <Icone glifo={IconeIa} papel="acao" />
                        por que errei?
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <div className="fim__acoes fim__bloco">
            {esgotada ? (
              <Link className="botao" to="/acervo?aba=importar">
                <Icone glifo="importar" papel="acao" />
                Importar mais questões
              </Link>
            ) : (
              <button type="button" className="botao" onClick={aoEstudarMais} disabled={encerrando}>
                {encerrando ? "Fechando…" : `Estudar mais ${atual.metaDiaria}`}
              </button>
            )}
            <button
              type="button"
              className="link-mao link-mao--tinta"
              data-tour="encerrar"
              onClick={encerrar}
              disabled={encerrando}
            >
              encerrar o dia
            </button>
          </div>
          {esgotada && painel && (
            <p className="t-ajuda t-2 fim__bloco">
              {painel.itensTotal === 1
                ? "A questão da trilha já foi respondida."
                : `As ${painel.itensTotal} questões da trilha já foram respondidas.`}
            </p>
          )}
        </div>

        <aside className="fim__lado">
          {ofensiva && (
            <div className="postit fim__postit" data-tour="ofensiva">
              <p className="t-anotacao-p">dias seguidos</p>
              <p className="fim__dias" aria-label={`${ofensiva.atual} dias seguidos`}>
                {cumpriu && antes !== null && antes !== ofensiva.atual && (
                  <span className="fim__dias-antes" aria-hidden="true">
                    {antes}
                    <svg viewBox="0 0 40 20" aria-hidden="true">
                      <path pathLength={100} d="M2 14 L38 6" />
                    </svg>
                  </span>
                )}
                <span className="fim__dias-agora">{ofensiva.atual}</span>
              </p>
              <p className="t-anotacao-p">
                {cumpriu ? `recorde ${ofensiva.recorde}` : `faltam ${faltam} para virar ${ofensiva.atual + 1}`}
              </p>
            </div>
          )}
          {painel && (
            <section className="fim__semana fim__bloco" aria-labelledby="fim-semana">
              <h2 className="t-secao" id="fim-semana">
                Esta semana
              </h2>
              <Semana painel={painel} animarHoje={cumpriu} />
              {descanso && <p className="t-ajuda">{descanso}</p>}
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}

function comoDuracao(segundos: number): string {
  const h = Math.floor(segundos / 3600);
  const m = Math.floor((segundos % 3600) / 60);
  const s = segundos % 60;
  if (h > 0) return `${h}h${String(m).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}
